/**
 * File Uploader Component
 *
 * Provides file picker and drag-drop interface for uploading files to clipboard.
 */
import { useState, useRef, useCallback } from 'react';
import type { PasteOptions, PasteResult } from '@amorphous/core';
import './FileUploader.css';

interface FileUploaderProps {
  /** Called when file is uploaded */
  onUpload: (data: Uint8Array, options: PasteOptions) => Promise<PasteResult>;
  /** Optional callback for upload progress (0-1) */
  onProgress?: (progress: number) => void;
  /** Whether to accept multiple files */
  multiple?: boolean;
  /** Accepted file types (e.g., "image/*,.pdf") */
  accept?: string;
  /** Optional class name */
  className?: string;
  /** Compact mode (just button, no drop zone) */
  compact?: boolean;
}

interface UploadState {
  status: 'idle' | 'reading' | 'uploading' | 'success' | 'error';
  progress: number;
  filename?: string;
  error?: string;
}

export function FileUploader({
  onUpload,
  onProgress,
  multiple = true,
  accept,
  className = '',
  compact = false,
}: FileUploaderProps) {
  const [state, setState] = useState<UploadState>({ status: 'idle', progress: 0 });
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle file selection
  const handleFiles = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setState({ status: 'reading', progress: 0, filename: file.name });

      try {
        // Read file as ArrayBuffer
        const buffer = await readFile(file, (progress) => {
          setState(s => ({ ...s, progress: progress * 0.5 })); // 0-50% for reading
          onProgress?.(progress * 0.5);
        });

        setState(s => ({ ...s, status: 'uploading', progress: 0.5 }));

        // Upload to clipboard
        const options: PasteOptions = {
          filename: file.name,
          label: file.name,
        };

        const result = await onUpload(new Uint8Array(buffer), options);

        if (result.success) {
          setState({ status: 'success', progress: 1, filename: file.name });
          onProgress?.(1);
          // Reset after showing success briefly
          setTimeout(() => setState({ status: 'idle', progress: 0 }), 2000);
        } else {
          setState({
            status: 'error',
            progress: 0,
            filename: file.name,
            error: 'Upload failed',
          });
        }
      } catch (err) {
        setState({
          status: 'error',
          progress: 0,
          filename: file.name,
          error: err instanceof Error ? err.message : 'Unknown error',
        });
      }
    }
  }, [onUpload, onProgress]);

  // Read file with progress
  const readFile = (file: File, onProgress: (progress: number) => void): Promise<ArrayBuffer> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onprogress = (e) => {
        if (e.lengthComputable) {
          onProgress(e.loaded / e.total);
        }
      };

      reader.onload = () => {
        if (reader.result instanceof ArrayBuffer) {
          resolve(reader.result);
        } else {
          reject(new Error('Failed to read file as ArrayBuffer'));
        }
      };

      reader.onerror = () => reject(reader.error);
      reader.readAsArrayBuffer(file);
    });
  };

  // Handle click on drop zone
  const handleClick = () => {
    fileInputRef.current?.click();
  };

  // Handle file input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  // Hidden file input
  const fileInput = (
    <input
      ref={fileInputRef}
      type="file"
      multiple={multiple}
      accept={accept}
      onChange={handleInputChange}
      style={{ display: 'none' }}
    />
  );

  // Compact mode: just a button
  if (compact) {
    return (
      <div className={`file-uploader compact ${className}`}>
        {fileInput}
        <button
          type="button"
          className="file-upload-btn"
          onClick={handleClick}
          disabled={state.status === 'reading' || state.status === 'uploading'}
        >
          {state.status === 'idle' && '📎 Attach File'}
          {state.status === 'reading' && `Reading ${state.filename}...`}
          {state.status === 'uploading' && `Uploading ${state.filename}...`}
          {state.status === 'success' && '✓ Uploaded'}
          {state.status === 'error' && '✗ Error'}
        </button>
      </div>
    );
  }

  // Full mode: drop zone with visual feedback
  return (
    <div className={`file-uploader ${className}`}>
      {fileInput}

      <div
        className={`drop-zone ${isDragOver ? 'drag-over' : ''} ${state.status}`}
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {state.status === 'idle' && (
          <>
            <div className="drop-icon">📁</div>
            <div className="drop-text">
              <strong>Drop files here</strong>
              <span>or click to browse</span>
            </div>
            <div className="drop-hint">
              Supports images, videos, PDFs, code, and more
            </div>
          </>
        )}

        {state.status === 'reading' && (
          <>
            <div className="progress-icon">📄</div>
            <div className="progress-text">
              Reading {state.filename}...
            </div>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${state.progress * 100}%` }}
              />
            </div>
          </>
        )}

        {state.status === 'uploading' && (
          <>
            <div className="progress-icon">⬆️</div>
            <div className="progress-text">
              Uploading {state.filename}...
            </div>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${state.progress * 100}%` }}
              />
            </div>
          </>
        )}

        {state.status === 'success' && (
          <>
            <div className="success-icon">✓</div>
            <div className="success-text">
              {state.filename} uploaded successfully
            </div>
          </>
        )}

        {state.status === 'error' && (
          <>
            <div className="error-icon">✗</div>
            <div className="error-text">
              Failed to upload {state.filename}
            </div>
            <div className="error-detail">{state.error}</div>
            <button
              className="retry-btn"
              onClick={(e) => {
                e.stopPropagation();
                setState({ status: 'idle', progress: 0 });
              }}
            >
              Try Again
            </button>
          </>
        )}
      </div>
    </div>
  );
}
